import { makeEsArticle, type EsArticleInput } from "./learnFactoryEs";
import type { LearnFAQ } from "@/pages/learn/learnShared";

/**
 * /es/learn/ampa-tms-cost — versión en español de /learn/ampa-tms-cost
 */

const AMPA_TRADEMARK =
  "Ampa y Ampa One son marcas registradas de Ampa Inc. Neuro Wellness TMS Centers of America es un proveedor independiente y no pertenece a Ampa.";

const faqs: LearnFAQ[] = [
  {
    question: "¿Cuánto cuesta el TMS Ampa One?",
    answer:
      "Un curso estándar de Ampa One se factura como cualquier otro TMS y suele estar cubierto por el seguro tras una autorización previa. Los programas acelerados y de un día suelen ser de pago directo, y le cotizamos su precio exacto de pago directo en su consulta gratuita. Verificamos sus beneficios del seguro sin costo antes de que se comprometa, para que conozca su costo real desde el principio.",
  },
  {
    question: "¿Cubre el seguro Ampa One?",
    answer:
      "Un curso estándar de Ampa One para la depresión suele estar cubierto por el seguro tras una autorización previa, porque el TMS para el trastorno depresivo mayor es un beneficio cubierto en la mayoría de los planes. Los programas acelerados y de un día son más recientes, por lo que la mayoría de los planes aún no los cubre y suelen ser de pago directo. La cobertura varía según el plan y verificamos sus beneficios sin costo.",
  },
  {
    question: "¿Cubre Medicare Ampa One?",
    answer:
      "El TMS estándar para la depresión es un beneficio cubierto por Medicare cuando se cumplen los criterios (un diagnóstico de depresión y ensayos previos con antidepresivos). Ampa One es un dispositivo de TMS autorizado por la FDA, por lo que un curso estándar facturado como TMS puede estar cubierto por Medicare. Los programas acelerados y de un día no están cubiertos por separado por Medicare. Confirmamos su plan específico y nuestra participación con Medicare en su consulta gratuita. La cobertura varía según el plan.",
  },
  {
    question: "¿Puedo usar mi HSA o FSA para el TMS Ampa?",
    answer:
      "Muchos pacientes usan fondos de HSA o FSA para la parte de su responsabilidad del TMS (copagos, coseguro o pago directo). Como el TMS para la depresión es un gasto médico calificado, los fondos de HSA/FSA generalmente pueden aplicarse; confirmamos si aceptamos HSA/FSA y qué planes de financiamiento o pago ofrecemos en su consulta. Consulte con el administrador de su plan.",
  },
  {
    question: "¿Está cubierto el TMS de un día por el seguro?",
    answer:
      "Por lo general, no. El TMS de un día comprime un curso completo en un solo día, y ese programa es más reciente y emergente, por lo que la mayoría de los planes aún no lo cubre. El dispositivo Ampa One en sí está autorizado por la FDA, pero el programa de un día suele ser de pago directo. Un curso estándar es el que más se cubre. La cobertura varía según el plan.",
  },
  {
    question: "¿Por qué varían tanto los precios del TMS?",
    answer:
      "Los precios del TMS varían según el programa (estándar, acelerado o de un día), según si aplica el seguro y según lo que se incluya (mapeo, sesiones, equipo clínico, seguimiento). Un curso estándar suele estar cubierto por el seguro. Los programas acelerados y de un día suelen ser de pago directo porque son más recientes. La marca del dispositivo importa menos que si el programa está autorizado por la FDA y si un psiquiatra supervisa su atención.",
  },
];

export const esAmpaTmsCost = makeEsArticle({
  slug: "ampa-tms-cost",
  title:
    "Costo del TMS Ampa One en Florida (2026): Precio, Seguro y Opciones de Pago",
  description:
    "Cuánto cuesta el TMS Ampa One en Florida: precios de curso estándar, acelerado y de un día, cobertura del seguro, Medicare, HSA/FSA y financiamiento. Verificación de beneficios sin costo.",
  h1: "¿Cuánto cuesta el TMS Ampa One?",
  breadcrumbLabel: "Costo del TMS Ampa One",
  shortAnswer:
    "Un curso estándar de Ampa One se factura como cualquier otro TMS para la depresión y suele estar cubierto por el seguro tras una autorización previa. Los programas acelerados y de un día son más recientes y emergentes, por lo que la mayoría de los planes aún no los cubre y suelen ser de pago directo. Le cotizamos su precio exacto de pago directo en su consulta gratuita. El número más útil es su costo real de bolsillo tras los beneficios, que verificamos sin costo antes de que se comprometa. Los resultados individuales varían. No se garantiza ningún resultado. La cobertura varía según el plan. Ampa One está disponible con receta tras una evaluación clínica.",
  keyTakeaways: [
    "Un curso estándar de Ampa One suele estar cubierto por el seguro tras una autorización previa.",
    "Los programas acelerados y de un día son más recientes y suelen ser de pago directo.",
    "El TMS para la depresión se factura con los códigos CPT 90867–90869; los planes suelen requerir un diagnóstico de depresión, ensayos fallidos con antidepresivos y autorización previa.",
    "Los fondos de HSA/FSA generalmente pueden aplicarse a la parte de su responsabilidad.",
    "Menos días de tratamiento pueden significar menos tiempo libre del trabajo, viajes y cuidado de niños.",
    "Verificamos sus beneficios sin costo antes de que se comprometa.",
  ],
  sections: [
    {
      heading: "Respuesta rápida: lo que cuesta el TMS Ampa One",
      paragraphs: [
        "Un curso estándar de Ampa One se factura como cualquier otro TMS para la depresión y suele estar cubierto por el seguro tras una autorización previa. Los programas acelerados y de un día son más recientes y emergentes, por lo que la mayoría de los planes aún no los cubre y suelen ser de pago directo. Le cotizamos su precio exacto de pago directo en su consulta gratuita. El número más útil es su costo real de bolsillo tras los beneficios, que verificamos sin costo antes de que se comprometa.",
      ],
    },
    {
      heading: "Precio por programa en Neuro Wellness",
      paragraphs: [
        "La tabla siguiente muestra cómo se factura típicamente cada programa. Confirmamos su precio exacto tras una verificación de beneficios sin costo.",
      ],
      table: {
        headers: ["Programa", "Precio típico en Neuro Wellness", "Seguro"],
        rows: [
          [
            "Curso estándar",
            "Suele cubrirse por el seguro tras autorización previa",
            "Suele cubrirse tras autorización previa",
          ],
          [
            "Acelerado / 2 días",
            "Suele ser de pago directo (cotización en la consulta)",
            "Suele ser de pago directo",
          ],
          [
            "Un día",
            "Suele ser de pago directo (cotización en la consulta)",
            "Suele ser de pago directo",
          ],
        ],
      },
    },
    {
      heading: "Cómo funciona el seguro para el TMS",
      paragraphs: [
        "El TMS para la depresión se factura con los códigos CPT 90867 (tratamiento inicial), 90868 (tratamiento posterior) y 90869 (tratamiento final). Los planes suelen requerir un diagnóstico de trastorno depresivo mayor, ensayos documentados fallidos con antidepresivos y autorización previa antes de aprobar el TMS. Un curso estándar de Ampa One cumple estos requisitos igual que cualquier TMS autorizado por la FDA.",
        "Los programas acelerados y de un día son más recientes, por lo que la mayoría de los planes aún no tienen una política de pago para ellos, por lo que suelen ser de pago directo. La cobertura varía según el plan. Verificamos sus beneficios sin costo para que sepa qué aplica antes de empezar.",
      ],
    },
    {
      heading: "Medicare, VA y los planes que aceptamos",
      paragraphs: [
        "El TMS estándar para la depresión es un beneficio cubierto por Medicare cuando se cumplen los criterios. Confirmamos su plan específico y nuestra participación con Medicare en su consulta gratuita. Para planes comerciales y el VA, consulte [[Costo y seguro|/es/cost-insurance]] y [[Cobertura del seguro para SAINT® TMS|/es/saint-tms-insurance-coverage]]. Verificamos su plan específico sin costo.",
      ],
    },
    {
      heading: "HSA/FSA, financiamiento y planes de pago",
      paragraphs: [
        "Muchos pacientes usan fondos de HSA o FSA para la parte de su responsabilidad del TMS (copagos, coseguro o pago directo), porque el TMS para la depresión es un gasto médico calificado. Confirmamos si aceptamos HSA/FSA y qué planes de financiamiento o pago ofrecemos en su consulta. El financiamiento está disponible caso por caso.",
      ],
    },
    {
      heading: "Por qué el costo total puede ser menor de lo que parece",
      paragraphs: [
        "Un programa acelerado o de un día significa menos días libres del trabajo, menos viajes a la clínica y menos cuidado de niños y viajes, lo que puede reducir el costo real total del tratamiento en comparación con un curso estándar de seis semanas. Para pacientes que viajan desde fuera del sur de Florida, consulte [[TMS Ampa One en Florida|/es/learn/ampa-tms-florida]].",
      ],
    },
    {
      heading: "Verificación de beneficios sin costo",
      paragraphs: [
        "Antes de comprometerse con cualquier programa, verificamos sus beneficios del seguro sin costo para que conozca su costo real de bolsillo. Llame al 954-827-9793 o solicite una consulta gratuita.",
      ],
    },
  ],
  faqs,
  sources: [
    {
      citation:
        "American Medical Association. Códigos CPT 90867–90869 — Tratamiento de estimulación magnética transcraneal (TMS).",
    },
    {
      citation:
        "Ampa Inc. Ampa One — sistema de estimulación magnética transcraneal autorizado por la FDA para el trastorno depresivo mayor.",
      url: "https://www.ampahealth.com/",
    },
  ],
  relatedLinks: [
    {
      title: "TMS Ampa One™ en Florida",
      href: "/es/ampa",
      description: "Tratamiento de depresión acelerado y guiado por cámara",
    },
    {
      title: "TMS de un día en Florida",
      href: "/es/one-day-tms",
      description: "El protocolo Ampa One Day explicado",
    },
    {
      title: "Costo de SAINT® TMS",
      href: "/es/learn/saint-tms-cost",
      description: "Lo que cuesta el protocolo de 5 días guiado por fMRI",
    },
    {
      title: "Costo y seguro",
      href: "/es/cost-insurance",
      description: "Verificación de beneficios y opciones de pago",
    },
    {
      title: "Solicitar una consulta",
      href: "/es/contactus",
      description: "Reserve una consulta gratuita con nuestro equipo clínico",
    },
  ],
  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    reviewedByHref: "/dr-daniel-pietras-md",
    published: "3 de noviembre de 2026",
    lastReviewed: "3 de noviembre de 2026",
    lastUpdated: "3 de noviembre de 2026",
  },
  toc: [
    {
      label: "Respuesta rápida",
      href: "#respuesta-rapida-lo-que-cuesta-el-tms-ampa-one",
    },
    {
      label: "Precio por programa",
      href: "#precio-por-programa-en-neuro-wellness",
    },
    {
      label: "Cómo funciona el seguro",
      href: "#como-funciona-el-seguro-para-el-tms",
    },
    {
      label: "Medicare y planes",
      href: "#medicare-va-y-los-planes-que-aceptamos",
    },
    {
      label: "HSA/FSA y financiamiento",
      href: "#hsafsa-financiamiento-y-planes-de-pago",
    },
    {
      label: "Costo total",
      href: "#por-que-el-costo-total-puede-ser-menor-de-lo-que-parece",
    },
    {
      label: "Verificación sin costo",
      href: "#verificacion-de-beneficios-sin-costo",
    },
    { label: "Preguntas frecuentes", href: "#faq" },
    { label: "Investigación citada", href: "#cited-research" },
  ],
  safetyBlock:
    "Los resultados individuales varían. No se garantiza ningún resultado. La cobertura varía según el plan. Ampa One está disponible con receta tras una evaluación clínica. Ampa One está autorizado por la FDA para el trastorno depresivo mayor en adultos que no han mejorado lo suficiente con medicación antidepresiva. Los efectos secundarios comunes incluyen molestias leves en el cuero cabelludo y dolor de cabeza que suelen desaparecer. Si está en crisis, llame o envíe un mensaje de texto al 988.",
  trademarkFooter: AMPA_TRADEMARK,
  ctaBand: {
    headline: "Verifique sus beneficios de Ampa One, sin costo",
    primaryText: "Verificar mi seguro",
    primaryHref: "/es/check-insurance-options",
    secondaryText: "Solicitar una consulta gratuita",
    secondaryHref: "/es/ampa",
    phone: "954-827-9793",
  },
} as EsArticleInput);
